/**
 * Invoice schemas
 */
import { z } from "zod";
import { createEntityFilterSchema, CurrencyCodeSchema, InvoiceDirectionSchema, InvoiceSeriesSchema, InvoiceStatusSchema, InvoiceTypeCodeSchema, InvoiceTypeSchema, NestedPaginationParamsSchema, ReferrerTypeSchema, SortParamsSchema, StrapiEntitySchema, StrapiFilterOps, StrapiIdSchema, VerifactuStatusSchema, } from "./common.schemas";
import { InvoiceRelationSchema } from "./relations";
import { LazyChargeRelation, LazyCommissionRelation, LazyCompanyRelation, LazyInvoiceRelation, LazyPayoutRelation, LazyUserRelation, } from "./relations";
// ============================================================================
// Enums
// ============================================================================
// InvoiceStatusSchema, InvoiceSeriesSchema, InvoiceTypeCodeSchema,
// InvoiceDirectionSchema, and InvoiceTypeSchema are imported from
// common.schemas.ts and re-exported above to avoid circular dependencies.
/**
 * Operation code schema - Spanish tax operation codes
 */
export const OperationCodeSchema = z.enum([
    "S1", // Subject and not exempt
    "S2", // Subject with exemption
    "N1", // Not subject - internal
    "N2", // Not subject - location rules
]);
// VerifactuStatusSchema is imported from common.schemas.ts
/**
 * Tax report type schema - matches Strapi enum
 */
export const TaxReportTypeSchema = z.enum(["MODELO_303", "MODELO_349", "MODELO_111", "MODELO_190"]);
/**
 * Tax report quarter schema - matches Strapi enum
 */
export const TaxReportQuarterSchema = z.enum(["Q1", "Q2", "Q3", "Q4", "ANNUAL"]);
/**
 * Rectification reason schema
 */
export const RectificationReasonSchema = z.enum([
    "full_refund",
    "partial_refund",
    "error_correction",
    "price_adjustment",
]);
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Invoice filters schema - typesafe filters for invoice queries
 * Created using createEntityFilterSchema to reuse field types from InvoiceRelationSchema
 */
export const InvoiceFiltersSchema = createEntityFilterSchema(InvoiceRelationSchema, {
    scalars: ["status", "invoiceSeries", "direction", "type", "invoiceNumber", "currency"],
    relations: ["user", "charge"],
}).extend({
    // Date range filters with $gte/$lte support
    issueDate: StrapiFilterOps(z.string()).optional(),
    invoiceDate: StrapiFilterOps(z.string()).optional(),
    dueDate: StrapiFilterOps(z.string()).optional(),
});
/**
 * Invoice query params schema
 */
export const InvoiceQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: InvoiceFiltersSchema.optional(),
    /**
     * ISO instant. Supported by the pending-manual-invoices endpoint to return
     * only invoices already past their due date; that endpoint does not read
     * the generic `filters` object.
     */
    dueBefore: z.string().optional(),
})
    .strict();
/**
 * VIES verification input schema
 */
export const ViesVerificationInputSchema = z
    .object({
    vatNumber: z.string().min(1, "VAT number is required"),
    userId: StrapiIdSchema.optional(),
})
    .strict();
/**
 * Tax profile validation input schema
 */
export const TaxProfileValidationInputSchema = z
    .object({
    userId: StrapiIdSchema,
    thresholds: z
        .object({
        minAmount: z.number().optional(),
        maxAmount: z.number().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Classify referrer input schema
 */
export const ClassifyReferrerInputSchema = z
    .object({
    userId: StrapiIdSchema.optional(),
    country: z.string().optional(),
    vatNumber: z.string().optional(),
    businessType: z.enum(["individual", "company"]).optional(),
})
    .strict();
/**
 * Payment eligibility input schema
 */
export const PaymentEligibilityInputSchema = z
    .object({
    userId: StrapiIdSchema,
    amount: z.number().positive("Amount must be positive"),
})
    .strict();
/**
 * Claim confirm input schema
 */
export const ClaimConfirmInputSchema = z
    .object({
    amount: z.number().positive().optional(),
    paymentMethod: z.enum(["bank_transfer", "crypto"]).optional(),
    walletAddress: z.string().optional(),
})
    .strict();
/**
 * Tax report query params schema
 */
export const TaxReportQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        type: z.object({ $eq: TaxReportTypeSchema.optional() }).strict().optional(),
        year: z.object({ $eq: z.number().optional() }).strict().optional(),
        status: z.object({ $eq: z.string().optional() }).strict().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Tax report generate input schema
 */
export const TaxReportGenerateInputSchema = z
    .object({
    type: TaxReportTypeSchema,
    year: z.number().int().min(2000).max(2100),
    quarter: TaxReportQuarterSchema,
})
    .strict();
/**
 * Manual payment input schema
 */
export const ManualPaymentInputSchema = z
    .object({
    invoiceId: StrapiIdSchema,
    amount: z.number().positive("Amount must be positive"),
    currency: CurrencyCodeSchema.optional(),
    paymentMethod: z.enum(["bank_transfer", "check", "cash", "other"]),
    reference: z.string().optional(),
    paidAt: z.string().optional(),
    notes: z.string().optional(),
})
    .strict();
/**
 * Admin invoice preview/generate input schema
 * Used by admin-invoice.previewInvoice and admin-invoice.generateTestInvoice
 */
export const AdminInvoicePreviewInputSchema = z
    .object({
    referrerId: StrapiIdSchema,
    amount: z.number().positive("Amount must be positive"),
    description: z.string().optional(),
})
    .strict();
/**
 * Admin list referrers query params
 * Used by admin-invoice.listReferrers
 */
export const AdminListReferrersQueryParamsSchema = z
    .object({
    search: z.string().optional(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
})
    .strict();
/**
 * Admin list invoices query params
 * Used by admin-invoice.listInvoices
 */
export const AdminListInvoicesQueryParamsSchema = z
    .object({
    year: z.number().int().optional(),
    type: InvoiceSeriesSchema.optional(),
    status: InvoiceStatusSchema.optional(),
    isTest: z.enum(["true", "false"]).optional(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
})
    .strict();
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Invoice line item schema
 */
export const InvoiceLineItemSchema = StrapiEntitySchema.extend({
    description: z.string(),
    quantity: z.number(),
    unitPrice: z.number(),
    amount: z.number(),
    taxRate: z.number().nullish(),
    taxAmount: z.number().nullish(),
});
/**
 * Invoice issuer/recipient details schema
 */
export const InvoicePartyDetailsSchema = z.object({
    name: z.string().nullish(),
    taxId: z.string().nullish(),
    taxIdType: z.string().nullish(),
    address: z.string().nullish(),
    city: z.string().nullish(),
    postalCode: z.string().nullish(),
    country: z.string().nullish(),
    countryCode: z.string().nullish(),
});
/**
 * Invoice schema - matches Strapi content type with Spanish tax compliance
 */
export const InvoiceSchema = StrapiEntitySchema.extend({
    // Identifiers
    invoiceNumber: z.string(),
    invoiceSeries: InvoiceSeriesSchema.nullish(),
    invoiceType: InvoiceTypeCodeSchema.nullish(), // Strapi field name is invoiceType
    invoiceTypeCode: InvoiceTypeCodeSchema.nullish(), // Alias for backwards compat
    direction: InvoiceDirectionSchema.nullish(),
    reference: z.string().nullish(),
    // Type and status
    type: InvoiceTypeSchema.nullish(),
    status: InvoiceStatusSchema,
    // Amounts (all field names match Strapi schema)
    baseAmount: z.number().nullish(),
    subtotal: z.number().nullish(),
    vatAmount: z.number().nullish(),
    irpfAmount: z.number().nullish(),
    netPayable: z.number().nullish(),
    totalAmount: z.number().nullish(),
    taxAmount: z.number().nullish(),
    total: z.number().nullish(),
    currency: CurrencyCodeSchema.nullish(),
    // Spanish tax rates
    vatRate: z.number().nullish(),
    irpfRate: z.number().nullish(),
    operationCode: OperationCodeSchema.nullish(),
    isReverseCharge: z.boolean().nullish(),
    taxRegime: z.string().nullish(),
    legalText: z.string().nullish(),
    // Issuer details (flattened for Strapi)
    issuerDetails: InvoicePartyDetailsSchema.nullish(),
    issuerName: z.string().nullish(),
    issuerTaxId: z.string().nullish(),
    issuerAddress: z.string().nullish(),
    issuerCountry: z.string().nullish(),
    // Recipient details (flattened for Strapi)
    recipientDetails: InvoicePartyDetailsSchema.nullish(),
    recipientName: z.string().nullish(),
    recipientTaxId: z.string().nullish(),
    recipientAddress: z.string().nullish(),
    recipientCountry: z.string().nullish(),
    // Referrer info
    referrerType: ReferrerTypeSchema.nullish(),
    // Dates (invoiceDate is the actual Strapi field, issueDate is alias)
    invoiceDate: z.string().nullish(),
    issueDate: z.string().nullish(),
    dueDate: z.string().nullish(),
    paidAt: z.string().nullish(),
    // Description
    description: z.string().nullish(),
    notes: z.string().nullish(),
    // Rectification
    rectificationReason: RectificationReasonSchema.nullish(),
    rectificationDescription: z.string().nullish(),
    // Manual payment tracking
    manualPaymentDetails: z.record(z.unknown()).nullish(),
    manualPaymentRecordedAt: z.string().nullish(),
    // Verifactu fields
    verifactu_id: z.number().nullish(),
    verifactu_status: VerifactuStatusSchema.nullish(),
    verifactu_error: z.string().nullish(),
    // PDF
    pdfUrl: z.string().nullish(),
    // Relations (populated) - using lazy schemas to break circular deps
    issuer: LazyCompanyRelation.nullish(),
    recipient: LazyUserRelation.nullish(),
    user: LazyUserRelation.nullish(),
    charge: LazyChargeRelation.nullish(),
    payout: LazyPayoutRelation.nullish(),
    commission: LazyCommissionRelation.nullish(),
    originalInvoice: LazyInvoiceRelation.nullish(),
    manualPaymentRecordedBy: LazyUserRelation.nullish(),
    lineItems: z.array(InvoiceLineItemSchema).nullish(),
});
/**
 * Invoice series schema (entity for tracking series numbers)
 */
export const InvoiceSeriesEntitySchema = StrapiEntitySchema.extend({
    name: z.string(),
    prefix: z.string(),
    currentNumber: z.number(),
    year: z.number(),
    isActive: z.boolean(),
});
/**
 * VIES verification result schema
 * Matches: apps/api/src/api/invoice/controllers/custom/verifyVIES.ts
 */
export const ViesVerificationResultSchema = z.object({
    success: z.boolean(),
    valid: z.boolean(),
    vatNumber: z.string(),
    countryCode: z.string(),
    name: z.string().nullish(),
    address: z.string().nullish(),
    verifiedAt: z.string().nullish(),
    expiresAt: z.string().nullish(),
    error: z.string().nullish(),
});
/**
 * Referrer classification schema
 */
export const ReferrerClassificationSchema = z.object({
    type: z.enum(["individual", "company"]),
    taxTreatment: z.enum(["standard", "reverse_charge", "exempt"]),
    vatApplicable: z.boolean(),
    withholdingTax: z.number().nullish(),
});
/**
 * Tax profile validation result schema
 */
export const TaxProfileValidationResultSchema = z.object({
    isValid: z.boolean(),
    errors: z.array(z.string()).nullish(),
    warnings: z.array(z.string()).nullish(),
    classification: ReferrerClassificationSchema.nullish(),
});
/**
 * Payment eligibility result schema
 */
export const PaymentEligibilityResultSchema = z.object({
    eligible: z.boolean(),
    reason: z.string().nullish(),
    minimumAmount: z.number().nullish(),
    availableBalance: z.number().nullish(),
    pendingPayments: z.number().nullish(),
});
/**
 * Claim eligibility schema
 */
export const ClaimEligibilitySchema = z.object({
    eligible: z.boolean().nullish(),
    reason: z.string().nullish(),
    availableAmount: z.number().nullish(),
    minimumClaimAmount: z.number().nullish(),
    taxWithholding: z.number().nullish(),
    // Additional fields from ClaimFlowModal usage
    amount: z.number().nullish(),
    commissionCount: z.number().nullish(),
    reasons: z.array(z.string()).nullish(),
    warnings: z.array(z.string()).nullish(),
    taxProfileErrors: z.array(z.string()).nullish(),
    // Existing pending claim details (when PENDING_CLAIM_EXISTS)
    pendingClaim: z
        .object({
        id: z.union([z.number(), z.string()]),
        invoiceNumber: z.string().nullish(),
        amount: z.number().nullish(),
        createdAt: z.string().nullish(),
    })
        .nullish(),
});
/**
 * Claim invoice preview party schema
 */
const ClaimInvoicePartySchema = z.object({
    name: z.string().nullish(),
    taxId: z.string().nullish(),
});
/**
 * Claim invoice preview data schema
 */
export const ClaimInvoicePreviewDataSchema = z.object({
    netPayable: z.number().nullish(),
    baseAmount: z.number().nullish(),
    vatAmount: z.number().nullish(),
    vatRate: z.number().nullish(),
    irpfAmount: z.number().nullish(),
    irpfRate: z.number().nullish(),
    invoiceSeries: z.string().nullish(),
    referrerType: z.string().nullish(),
    legalText: z.string().nullish(),
    issuer: ClaimInvoicePartySchema.nullish(),
    recipient: ClaimInvoicePartySchema.nullish(),
});
/**
 * Claim preview schema
 */
export const ClaimPreviewSchema = z.object({
    // Original fields
    grossAmount: z.number().nullish(),
    taxAmount: z.number().nullish(),
    netAmount: z.number().nullish(),
    currency: CurrencyCodeSchema.nullish(),
    taxRate: z.number().nullish(),
    taxType: z.string().nullish(),
    // ClaimFlowModal expects preview field with invoice details
    preview: ClaimInvoicePreviewDataSchema.nullish(),
});
/**
 * Claim history item schema - matches Strapi claimHistory controller response
 * Unified format for both old payouts and new invoices
 */
export const ClaimHistoryItemSchema = z.object({
    id: StrapiIdSchema,
    _source: z.enum(["payout", "invoice"]),
    invoiceNumber: z.string().nullable(),
    amount: z.number(),
    currency: z.string(),
    status: z.string(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    // Old payout specific
    commissionCount: z.number().nullish(),
    // New invoice specific
    invoiceSeries: z.string().nullish(),
    direction: z.string().nullish(),
    referrerType: z.string().nullish(),
    baseAmount: z.number().nullish(),
    vatRate: z.number().nullish(),
    vatAmount: z.number().nullish(),
    irpfRate: z.number().nullish(),
    irpfAmount: z.number().nullish(),
    netPayable: z.number().nullish(),
    verifactuStatus: z.string().nullish(),
    isTest: z.boolean().nullish(),
});
/**
 * Claim history summary source breakdown schema
 */
export const ClaimHistorySourceSummarySchema = z.object({
    total: z.number(),
    processed: z.number().nullish(),
    confirmed: z.number().nullish(),
    pending: z.number(),
    totalAmount: z.number(),
});
/**
 * Claim history summary schema - matches Strapi claimHistory controller response
 */
export const ClaimHistorySummarySchema = z.object({
    totalClaims: z.number(),
    totalClaimed: z.number(),
    pendingAmount: z.number(),
    payouts: ClaimHistorySourceSummarySchema,
    invoices: ClaimHistorySourceSummarySchema,
});
/**
 * Claim history response schema - the full response from getClaimHistory
 */
export const ClaimHistoryResponseSchema = z.object({
    claims: z.array(ClaimHistoryItemSchema),
    summary: ClaimHistorySummarySchema,
});
/**
 * Claim result schema (response from confirmClaim)
 */
export const ClaimResultSchema = z.object({
    success: z.boolean().nullish(),
    error: z.string().nullish(),
    invoiceNumber: z.string().nullish(),
    netPayable: z.number().nullish(),
    payoutLinkUrl: z.string().nullish(),
    invoiceId: StrapiIdSchema.nullish(),
});
/**
 * Admin invoice preview result schema
 */
export const AdminInvoicePreviewResultSchema = z.object({
    preview: InvoiceSchema,
    warnings: z.array(z.string()).nullish(),
});
/**
 * Tax report status schema - matches Strapi enum
 */
export const TaxReportStatusSchema = z.enum(["draft", "generated", "submitted", "confirmed", "failed"]);
/**
 * Tax report schema - matches Strapi content type
 */
export const TaxReportSchema = StrapiEntitySchema.extend({
    type: TaxReportTypeSchema,
    year: z.number(),
    quarter: TaxReportQuarterSchema,
    status: TaxReportStatusSchema,
    generatedAt: z.string().nullish(),
    submittedAt: z.string().nullish(),
    confirmedAt: z.string().nullish(),
    periodStart: z.string(),
    periodEnd: z.string(),
    totalBaseAmount: z.number().nullish(),
    totalVATAmount: z.number().nullish(),
    totalIRPFAmount: z.number().nullish(),
    totalAutorepercusion: z.number().nullish(),
    transactionCount: z.number().nullish(),
    summary: z.unknown().nullish(),
    lineItems: z.unknown().nullish(),
    errorMessage: z.string().nullish(),
    notes: z.string().nullish(),
});
/**
 * Manual payment history query params schema
 * Used by /invoice/admin/manual-history endpoint
 */
export const ManualPaymentHistoryQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .extend({
    filters: z
        .object({
        status: z.object({ $eq: InvoiceStatusSchema.optional() }).strict().optional(),
        invoiceSeries: z.object({ $eq: InvoiceSeriesSchema.optional() }).strict().optional(),
        manualPaymentRecordedAt: StrapiFilterOps(z.string()).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
/**
 * Manual payment history item schema
 * Matches the response from /invoice/admin/manual-history
 * Returns invoice data with payment details
 */
export const ManualPaymentHistoryItemSchema = z.object({
    id: StrapiIdSchema,
    invoiceNumber: z.string(),
    invoiceDate: z.string().nullish(),
    invoiceSeries: InvoiceSeriesSchema.nullish(),
    recipientName: z.string().nullish(),
    baseAmount: z.number().nullish(),
    totalAmount: z.number().nullish(),
    netPayable: z.number().nullish(),
    currency: CurrencyCodeSchema.nullish(),
    status: InvoiceStatusSchema,
    manualPaymentDetails: z.record(z.unknown()).nullish(),
    manualPaymentRecordedAt: z.string().nullish(),
    user: z
        .object({
        id: StrapiIdSchema,
        username: z.string().nullish(),
        email: z.string(),
        name: z.string().nullish(),
    })
        .nullish(),
    recordedBy: z
        .object({
        id: StrapiIdSchema,
        email: z.string(),
    })
        .nullish(),
});
